import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { EventDrawer } from './EventDrawer';

/**
 * EventsPage — top-level Admin tab for managing date-bound events.
 *
 * Distinct from /api/activities (catalogue of holiday-club activity types).
 * Each Event is a one-off thing with attendees, allocated staff, resources,
 * risk assessment notes, and — for type='trip' — outings consent
 * verification per attendee.
 *
 * Layout:
 *   - Header with filters (date range + event_type + status) and
 *     "+ New event" CTA.
 *   - Card grid of upcoming events grouped by date.
 *   - Click a card → EventDrawer opens for editing / attendees / staff
 *     / consent check.
 */

const EVENT_TYPES = [
  { v: 'trip',           label: '🚌 Trip', tone: 'hsl(35 90% 92%)', fg: 'hsl(35 80% 30%)' },
  { v: 'in_house',       label: '🏠 In-house', tone: 'hsl(220 70% 92%)', fg: 'hsl(220 60% 30%)' },
  { v: 'visitor',        label: '👋 Visitor', tone: 'hsl(160 50% 90%)', fg: 'hsl(160 50% 28%)' },
  { v: 'parent_session', label: '👪 Parent session', tone: 'hsl(280 50% 92%)', fg: 'hsl(280 50% 30%)' },
  { v: 'celebration',    label: '🎉 Celebration', tone: 'hsl(340 70% 93%)', fg: 'hsl(340 60% 35%)' },
  { v: 'other',          label: 'Other', tone: 'hsl(220 15% 92%)', fg: 'hsl(220 15% 35%)' },
];

const STATUSES = [
  { v: 'draft',       label: 'Draft' },
  { v: 'scheduled',   label: 'Scheduled' },
  { v: 'in_progress', label: 'In progress' },
  { v: 'completed',   label: 'Completed' },
  { v: 'cancelled',   label: 'Cancelled' },
];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
function plusDays(iso, days) {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

// Roles allowed to create / edit / delete / manage attendees / notify
// parents from the Events UI. Lower roles still see the tab but with a
// read + register-only experience (registers and media remain editable
// to all staff via the drawer's own gates).
const EVENT_MANAGE_ROLES = new Set([
  'admin', 'org_owner', 'org_admin', 'deputy_manager', 'room_leader',
]);

export function EventsPage({ currentUser }) {
  const canManage = EVENT_MANAGE_ROLES.has(currentUser?.role);

  // Filters — default to next 90 days
  const initialFrom = useMemo(() => plusDays(todayIso(), -7), []);
  const initialTo = useMemo(() => plusDays(todayIso(), 90), []);

  const [dateFrom, setDateFrom] = useState(initialFrom);
  const [dateTo, setDateTo] = useState(initialTo);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState('');

  const [drawerEventId, setDrawerEventId] = useState(null);
  const [drawerNewEvent, setDrawerNewEvent] = useState(false);

  // View mode: 'list' or 'calendar'
  const [viewMode, setViewMode] = useState('list');
  // Calendar anchor (first of the displayed month)
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await api.listEvents({
        date_from: dateFrom,
        date_to: dateTo,
        event_type: typeFilter || undefined,
        status: statusFilter || undefined,
      });
      setEvents(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError(err.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, typeFilter, statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  // Group by date for the rendering
  const grouped = useMemo(() => {
    const map = new Map();
    for (const e of events) {
      const key = e.event_date;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [events]);

  const handleSave = async () => {
    setDrawerEventId(null);
    setDrawerNewEvent(false);
    await refresh();
  };

  return (
    <div data-testid="events-page">
      <div className="page-header" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h1 style={{ margin: 0 }}>📅 Events</h1>
          <p style={{ margin: 0, color: 'hsl(var(--muted-foreground))' }}>
            Trips, in-house themed days, visitors, parent sessions and celebrations.
          </p>
        </div>
        {/* View mode toggle */}
        <div
          role="tablist"
          aria-label="View mode"
          data-testid="events-view-toggle"
          style={{
            display: 'inline-flex',
            border: '1px solid hsl(var(--border))',
            borderRadius: 999,
            overflow: 'hidden',
            background: '#fff',
          }}
        >
          {[
            { v: 'list', label: '☰ List' },
            { v: 'calendar', label: '🗓 Calendar' },
          ].map((m) => {
            const on = viewMode === m.v;
            return (
              <button
                key={m.v}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => setViewMode(m.v)}
                data-testid={`events-view-${m.v}`}
                style={{
                  padding: '0.4rem 0.85rem',
                  border: 'none',
                  background: on ? 'hsl(var(--primary))' : 'transparent',
                  color: on ? 'hsl(var(--primary-foreground, 0 0% 100%))' : 'hsl(var(--foreground))',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {m.label}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => { setDrawerNewEvent(true); setDrawerEventId(null); }}
          data-testid="events-new-btn"
          style={{ padding: '0.55rem 1rem', display: canManage ? undefined : 'none' }}
        >
          + New event
        </button>
      </div>

      {/* Filters */}
      <div
        className="card"
        style={{
          display: 'flex',
          gap: '0.6rem',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          marginBottom: '1rem',
        }}
        data-testid="events-filters"
      >
        <label style={smallLabel}>
          <span>From</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={inputStyle}
            data-testid="events-filter-from"
          />
        </label>
        <label style={smallLabel}>
          <span>To</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={inputStyle}
            data-testid="events-filter-to"
          />
        </label>
        <label style={smallLabel}>
          <span>Type</span>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={inputStyle}
            data-testid="events-filter-type"
          >
            <option value="">All types</option>
            {EVENT_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
          </select>
        </label>
        <label style={smallLabel}>
          <span>Status</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={inputStyle}
            data-testid="events-filter-status"
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
        </label>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={refresh}
          data-testid="events-refresh"
          style={{ marginLeft: 'auto' }}
        >
          ↻ Refresh
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))' }}>
          Loading events…
        </div>
      ) : error ? (
        <div className="card" style={{ borderLeft: '4px solid hsl(0 60% 50%)', color: 'hsl(0 60% 40%)' }} data-testid="events-error">
          {error}
        </div>
      ) : viewMode === 'calendar' ? (
        <CalendarView
          events={events}
          month={calendarMonth}
          onPrev={() => setCalendarMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
          onNext={() => setCalendarMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
          onJumpToday={() => {
            const d = new Date();
            setCalendarMonth(new Date(d.getFullYear(), d.getMonth(), 1));
          }}
          onOpen={(id) => { setDrawerEventId(id); setDrawerNewEvent(false); }}
        />
      ) : grouped.length === 0 ? (
        <div className="empty-state" style={{ padding: '2rem' }} data-testid="events-empty">
          <p>No events in this date range.</p>
          <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.9rem' }}>
            Tap <strong>+ New event</strong> above to create one.
          </p>
        </div>
      ) : (
        grouped.map(([date, dayEvents]) => (
          <div key={date} style={{ marginBottom: '1.25rem' }} data-testid={`events-day-${date}`}>
            <h3 style={{ margin: '0 0 0.6rem 0', fontSize: '0.95rem', color: 'hsl(var(--muted-foreground))' }}>
              {formatDateHeader(date)}
            </h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '0.75rem',
              }}
            >
              {dayEvents.map((e) => {
                const typeMeta = EVENT_TYPES.find((t) => t.v === e.event_type) || EVENT_TYPES[5];
                return (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => { setDrawerEventId(e.id); setDrawerNewEvent(false); }}
                    data-testid={`event-card-${e.id}`}
                    style={{
                      textAlign: 'left',
                      padding: '0.9rem',
                      border: '1px solid hsl(var(--border))',
                      borderLeft: `4px solid ${typeMeta.fg}`,
                      borderRadius: 'var(--radius)',
                      background: '#fff',
                      cursor: 'pointer',
                      transition: 'transform 120ms ease, box-shadow 120ms ease',
                    }}
                    onMouseEnter={(el) => { el.currentTarget.style.transform = 'translateY(-2px)'; el.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)'; }}
                    onMouseLeave={(el) => { el.currentTarget.style.transform = 'none'; el.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <strong style={{ fontSize: '0.98rem', lineHeight: 1.3 }}>{e.title}</strong>
                      <span style={{ background: typeMeta.tone, color: typeMeta.fg, padding: '0.15rem 0.55rem', borderRadius: 999, fontSize: '0.72rem', fontWeight: 600 }}>
                        {typeMeta.label}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.82rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.3rem' }}>
                      {e.start_time && `${e.start_time}${e.end_time ? ' – ' + e.end_time : ''}`}
                      {e.location ? ` · ${e.location}` : ''}
                    </div>
                    <div style={{ marginTop: '0.55rem', display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                      <span style={statusPill(e.status)}>{(STATUSES.find((s) => s.v === e.status) || {}).label || e.status}</span>
                      <span style={countPill}>👶 {e.attendee_child_ids?.length || 0}{e.capacity ? `/${e.capacity}` : ''}</span>
                      <span style={countPill}>👥 {((e.supporting_staff_ids?.length || 0) + (e.lead_staff_id ? 1 : 0))}</span>
                      {e.event_type === 'trip' && !e.risk_assessment && (
                        <span style={{ ...countPill, background: 'hsl(0 75% 94%)', color: 'hsl(0 60% 35%)' }} title="No risk assessment recorded">⚠️ RA missing</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))
      )}

      {/* Drawer */}
      {(drawerEventId || drawerNewEvent) && (
        <EventDrawer
          eventId={drawerEventId}
          createNew={drawerNewEvent}
          currentUser={currentUser}
          canManage={canManage}
          onClose={() => { setDrawerEventId(null); setDrawerNewEvent(false); }}
          onSaved={handleSave}
        />
      )}
    </div>
  );
}

function formatDateHeader(iso) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch { return iso; }
}

function statusPill(status) {
  const map = {
    draft:       { bg: 'hsl(220 15% 92%)', fg: 'hsl(220 15% 35%)' },
    scheduled:   { bg: 'hsl(220 70% 92%)', fg: 'hsl(220 60% 30%)' },
    in_progress: { bg: 'hsl(45 95% 92%)',  fg: 'hsl(35 80% 30%)' },
    completed:   { bg: 'hsl(140 70% 92%)', fg: 'hsl(140 55% 26%)' },
    cancelled:   { bg: 'hsl(0 75% 94%)',   fg: 'hsl(0 60% 35%)' },
  };
  const t = map[status] || map.draft;
  return {
    background: t.bg, color: t.fg,
    padding: '0.1rem 0.5rem', borderRadius: 999,
    fontSize: '0.72rem', fontWeight: 600,
  };
}
const countPill = {
  background: 'hsl(var(--muted) / 0.6)',
  color: 'hsl(var(--foreground))',
  padding: '0.1rem 0.5rem', borderRadius: 999,
  fontSize: '0.72rem', fontWeight: 600,
};
const inputStyle = {
  padding: '0.45rem 0.55rem',
  border: '1px solid hsl(var(--border))',
  borderRadius: 'var(--radius)',
  fontSize: '0.85rem', background: '#fff',
};
const smallLabel = {
  display: 'flex', flexDirection: 'column', gap: '0.15rem',
  fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))',
  textTransform: 'uppercase',
};

// ---------------- Iteration B: Month calendar view ----------------

function CalendarView({ events, month, onPrev, onNext, onJumpToday, onOpen }) {
  // Build a 6×7 grid starting on Monday of the first week that contains
  // the 1st of the month.
  const year = month.getFullYear();
  const monthIdx = month.getMonth();
  const firstOfMonth = new Date(year, monthIdx, 1);
  // 0 = Sunday in JS; we want weeks starting Monday → shift.
  const startOffset = (firstOfMonth.getDay() + 6) % 7;
  const gridStart = new Date(year, monthIdx, 1 - startOffset);

  const days = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
    days.push(d);
  }

  // Group events by ISO date for quick lookup
  const byDate = new Map();
  for (const e of events) {
    if (!e.event_date) continue;
    if (!byDate.has(e.event_date)) byDate.set(e.event_date, []);
    byDate.get(e.event_date).push(e);
  }

  const todayIsoStr = new Date().toISOString().slice(0, 10);
  const monthLabel = month.toLocaleString(undefined, { month: 'long', year: 'numeric' });

  return (
    <div data-testid="events-calendar">
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        marginBottom: '0.6rem', flexWrap: 'wrap',
      }}>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onPrev}
          data-testid="events-calendar-prev"
          style={{ padding: '0.35rem 0.7rem' }}
        >‹ Prev</button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onJumpToday}
          data-testid="events-calendar-today"
          style={{ padding: '0.35rem 0.7rem' }}
        >Today</button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onNext}
          data-testid="events-calendar-next"
          style={{ padding: '0.35rem 0.7rem' }}
        >Next ›</button>
        <strong style={{ marginLeft: '0.5rem', fontSize: '1rem' }}>{monthLabel}</strong>
        <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>
          {events.length} event{events.length === 1 ? '' : 's'} in current filter
        </span>
      </div>

      {/* Day-of-week header */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
        gap: '0.25rem', marginBottom: '0.25rem',
      }}>
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <div key={d} style={{
            padding: '0.35rem 0.45rem', fontSize: '0.72rem',
            textTransform: 'uppercase', fontWeight: 600,
            color: 'hsl(var(--muted-foreground))', letterSpacing: '0.04em',
          }}>
            {d}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
        gap: '0.25rem',
      }} data-testid="events-calendar-grid">
        {days.map((d, i) => {
          const iso = d.toISOString().slice(0, 10);
          const inMonth = d.getMonth() === monthIdx;
          const isToday = iso === todayIsoStr;
          const dayEvents = byDate.get(iso) || [];
          return (
            <div
              key={i}
              data-testid={`calendar-cell-${iso}`}
              style={{
                minHeight: 92,
                background: inMonth ? '#fff' : 'hsl(var(--muted) / 0.35)',
                border: `1px solid ${isToday ? 'hsl(var(--primary))' : 'hsl(var(--border))'}`,
                borderRadius: 'var(--radius)',
                padding: '0.3rem 0.35rem',
                display: 'flex', flexDirection: 'column', gap: '0.2rem',
                opacity: inMonth ? 1 : 0.7,
              }}
            >
              <div style={{
                fontSize: '0.74rem', fontWeight: 700,
                color: isToday ? 'hsl(var(--primary))' : 'hsl(var(--foreground))',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span>{d.getDate()}</span>
                {isToday && (
                  <span style={{
                    fontSize: '0.6rem', textTransform: 'uppercase',
                    background: 'hsl(var(--primary))', color: '#fff',
                    padding: '0.05rem 0.35rem', borderRadius: 999,
                  }}>Today</span>
                )}
              </div>
              {dayEvents.slice(0, 3).map((e) => {
                const meta = EVENT_TYPES.find((t) => t.v === e.event_type) || EVENT_TYPES[5];
                return (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => onOpen(e.id)}
                    data-testid={`calendar-event-${e.id}`}
                    title={`${e.title}${e.start_time ? ' · ' + e.start_time : ''}`}
                    style={{
                      textAlign: 'left',
                      padding: '0.2rem 0.4rem',
                      borderRadius: 4,
                      borderLeft: `3px solid ${meta.fg}`,
                      background: meta.tone,
                      color: meta.fg,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      border: 'none',
                      borderLeftWidth: 3,
                      borderLeftStyle: 'solid',
                    }}
                  >
                    {e.start_time && <span style={{ opacity: 0.8, marginRight: 4 }}>{e.start_time.slice(0, 5)}</span>}
                    {e.title}
                  </button>
                );
              })}
              {dayEvents.length > 3 && (
                <span style={{ fontSize: '0.68rem', color: 'hsl(var(--muted-foreground))', paddingLeft: '0.2rem' }}>
                  +{dayEvents.length - 3} more
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default EventsPage;
